import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Progress } from './ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  ArrowLeft, BarChart3, Users, Package, TrendingUp, GraduationCap,
  Search, ChevronDown, ChevronUp, BookOpen, CheckCircle, Star, ArrowRight, FileText, HelpCircle, Wrench, ClipboardList, X
} from 'lucide-react';
import { toast } from 'sonner';

const API = BACKEND_URL;

const PACKAGE_CONFIG = {
  'pkg-small': { color: 'border-blue-400 bg-blue-50', badge: 'bg-blue-100 text-blue-800', icon: '🌱', accent: 'text-blue-700' },
  'pkg-medium': { color: 'border-purple-400 bg-purple-50', badge: 'bg-purple-100 text-purple-800', icon: '📈', accent: 'text-purple-700' },
  'pkg-large': { color: 'border-emerald-400 bg-emerald-50', badge: 'bg-emerald-100 text-emerald-800', icon: '🏆', accent: 'text-emerald-700' },
};

const CapacityBuilding = ({ user }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('progress');
  const [expandedId, setExpandedId] = useState(null);
  const [modules, setModules] = useState([]);
  const [packages, setPackages] = useState([]);
  const [activeTab, setActiveTab] = useState('packages');
  const [expandedPkg, setExpandedPkg] = useState(null);
  const [viewingContent, setViewingContent] = useState(null);
  const [contentLoading, setContentLoading] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, enrollRes, modulesRes, packagesRes] = await Promise.all([
        axios.get(`${API}/api/capacity-building/stats`, { headers }),
        axios.get(`${API}/api/capacity-building/enrollments?sort_by=${sortBy}`, { headers }),
        axios.get(`${API}/api/capacity-building/modules`, { headers }),
        axios.get(`${API}/api/capacity-building/packages`, { headers }),
      ]);
      setStats(statsRes.data);
      setEnrollments(enrollRes.data);
      setModules(modulesRes.data.modules || []);
      setPackages(packagesRes.data.packages || []);
    } catch (error) {
      console.error('Failed to fetch capacity building data:', error);
      setStats({ total_social_actors: 0, total_enrolled: 0, avg_progress: 0, total_modules: 10, package_distribution: {} });
      setEnrollments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSeed = async () => {
    try {
      await axios.post(`${API}/api/capacity-building/seed`, {}, { headers });
      toast.success('Sample data seeded successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to seed data');
    }
  };

  const handleViewContent = async (moduleId) => {
    setContentLoading(true);
    try {
      const res = await axios.get(`${API}/api/capacity-building/modules/${moduleId}/content`, { headers });
      setViewingContent(res.data);
    } catch (error) {
      toast.error('Failed to load module content');
    } finally {
      setContentLoading(false);
    }
  };

  const handleSort = (newSort) => {
    setSortBy(newSort);
    const sorted = [...enrollments];
    if (newSort === 'progress') sorted.sort((a, b) => (b.progress_percentage || 0) - (a.progress_percentage || 0));
    else if (newSort === 'name') sorted.sort((a, b) => (a.organization_name || '').localeCompare(b.organization_name || ''));
    else if (newSort === 'activity') sorted.sort((a, b) => (b.last_activity || '').localeCompare(a.last_activity || ''));
    setEnrollments(sorted);
  };

  const filteredEnrollments = enrollments.filter(e => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (e.organization_name || '').toLowerCase().includes(s) || (e.actor_name || '').toLowerCase().includes(s);
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'numeric', year: 'numeric' });
  };

  const enrollmentPackageColors = {
    'Starter Package': 'border-blue-300 text-blue-700 bg-blue-50',
    'Growth Package': 'border-purple-300 text-purple-700 bg-purple-50',
    'Excellence Package': 'border-emerald-300 text-emerald-700 bg-emerald-50',
  };

  const getModuleById = (id) => modules.find(m => m.id === id);

  const RESOURCE_ICONS = {
    guide: { icon: BookOpen, color: 'text-blue-600 bg-blue-50' },
    template: { icon: FileText, color: 'text-purple-600 bg-purple-50' },
    checklist: { icon: ClipboardList, color: 'text-green-700 bg-green-50' },
    faq: { icon: HelpCircle, color: 'text-amber-600 bg-amber-50' },
    tool: { icon: Wrench, color: 'text-teal-700 bg-teal-50' },
  };

  // If viewing module content, show the content viewer
  if (viewingContent) {
    return (
      <div className="space-y-6 p-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={() => setViewingContent(null)} data-testid="back-to-modules-btn" className="flex items-center gap-2">
            <ArrowLeft className="h-4 w-4" /> Back to Modules
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{viewingContent.title}</h1>
            <p className="text-sm text-gray-700 mt-1">{viewingContent.resources?.length || 0} training resources</p>
          </div>
        </div>

        <div className="space-y-6">
          {(viewingContent.resources || []).map((resource) => {
            const ri = RESOURCE_ICONS[resource.type] || RESOURCE_ICONS.guide;
            const Icon = ri.icon;
            return (
              <Card key={resource.id} data-testid={`resource-${resource.id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${ri.color}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{resource.title}</CardTitle>
                      <Badge variant="outline" className="mt-1 text-xs capitalize">{resource.type}</Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="prose prose-sm max-w-none text-gray-700">
                    {resource.content.split('\n').map((line, i) => {
                      const trimmed = line.trimStart();
                      if (trimmed.startsWith('## ')) return <h2 key={i} className="text-xl font-bold text-gray-900 mt-6 mb-3">{trimmed.replace('## ', '')}</h2>;
                      if (trimmed.startsWith('### ')) return <h3 key={i} className="text-lg font-semibold text-gray-800 mt-5 mb-2">{trimmed.replace('### ', '')}</h3>;
                      if (trimmed.startsWith('**') && trimmed.endsWith('**')) return <p key={i} className="font-semibold text-gray-800 mt-3">{trimmed.replace(/\*\*/g, '')}</p>;
                      if (trimmed.startsWith('- [ ] ')) { const txt = trimmed.replace('- [ ] ', ''); return <label key={i} className="flex items-start gap-2 ml-4 my-1"><input type="checkbox" className="mt-1" readOnly aria-label={txt} /><span className="text-sm">{txt}</span></label>; }
                      if (trimmed.startsWith('- ')) return <div key={i} className="flex items-start gap-2 ml-4 my-1"><span className="text-gray-700 mt-0.5">•</span><span className="text-sm">{trimmed.replace('- ', '')}</span></div>;
                      if (trimmed.startsWith('| ') && trimmed.includes('|')) {
                        const cells = trimmed.split('|').filter(c => c.trim());
                        if (cells.every(c => c.trim().match(/^[-:]+$/))) return null;
                        const isHeader = i > 0 && resource.content.split('\n')[i + 1]?.trim().startsWith('|') && resource.content.split('\n')[i + 1]?.includes('---');
                        return (
                          <div key={i} className={`grid gap-2 py-1.5 px-2 text-xs ${isHeader ? 'font-semibold bg-gray-100 rounded-t-lg' : 'border-b border-gray-100'}`}
                            style={{ gridTemplateColumns: `repeat(${cells.length}, minmax(0, 1fr))` }}>
                            {cells.map((cell, j) => <div key={j} className="truncate">{cell.trim().replace(/\*\*/g, '')}</div>)}
                          </div>
                        );
                      }
                      if (trimmed.startsWith('*') && trimmed.endsWith('*') && !trimmed.startsWith('**')) return <p key={i} className="text-sm italic text-gray-700 my-2">{trimmed.replace(/^\*|\*$/g, '')}</p>;
                      if (trimmed === '' || trimmed === '---') return <div key={i} className={trimmed === '---' ? 'border-t my-4' : 'h-2'} />;
                      if (/^\d+\./.test(trimmed)) return <div key={i} className="ml-4 my-1 text-sm">{trimmed}</div>;
                      return trimmed ? <p key={i} className="text-sm my-1.5 leading-relaxed">{trimmed}</p> : null;
                    })}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button data-testid="back-to-dashboard-btn" variant="outline" size="sm" onClick={() => navigate('/dashboard')} className="flex items-center gap-2">
            <ArrowLeft className="h-4 w-4" /> Dashboard
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <GraduationCap className="h-7 w-7 text-blue-600" /> Capacity Building
            </h1>
            <p className="text-gray-700 mt-1">Training packages, learning modules, and enrollment tracking</p>
          </div>
        </div>
        {(user?.role === 'administrator' || user?.role === 'project_manager') && enrollments.length === 0 && (
          <Button onClick={handleSeed} data-testid="seed-capacity-btn" className="bg-blue-600 hover:bg-blue-700">Initialize Sample Data</Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="p-4"><div className="flex items-center gap-3"><div className="p-2 bg-blue-50 rounded-lg"><Package className="h-5 w-5 text-blue-600" /></div><div><p className="text-2xl font-bold text-blue-700">{packages.length}</p><p className="text-sm text-gray-700">Packages</p></div></div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="flex items-center gap-3"><div className="p-2 bg-amber-50 rounded-lg"><GraduationCap className="h-5 w-5 text-amber-600" /></div><div><p className="text-2xl font-bold text-amber-700">{stats?.total_modules || 10}</p><p className="text-sm text-gray-700">Modules</p></div></div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="flex items-center gap-3"><div className="p-2 bg-green-50 rounded-lg"><Users className="h-5 w-5 text-green-700" /></div><div><p className="text-2xl font-bold text-green-700">{stats?.total_enrolled || 0}</p><p className="text-sm text-gray-700">Enrolled</p></div></div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="flex items-center gap-3"><div className="p-2 bg-green-50 rounded-lg"><TrendingUp className="h-5 w-5 text-green-700" /></div><div><p className="text-2xl font-bold text-green-700">{stats?.avg_progress || 0}%</p><p className="text-sm text-gray-700">Avg. Progress</p></div></div></CardContent></Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="packages" data-testid="tab-packages">Packages & Modules</TabsTrigger>
          <TabsTrigger value="modules" data-testid="tab-all-modules">All Modules</TabsTrigger>
          <TabsTrigger value="oversight" data-testid="tab-oversight">Enrollment Oversight</TabsTrigger>
        </TabsList>

        {/* ==================== PACKAGES TAB ==================== */}
        <TabsContent value="packages" className="space-y-6 mt-4">
          {packages.map((pkg) => {
            const cfg = PACKAGE_CONFIG[pkg.id] || PACKAGE_CONFIG['pkg-small'];
            const isExpanded = expandedPkg === pkg.id;
            return (
              <Card key={pkg.id} data-testid={`package-${pkg.id}`} className={`border-l-4 ${cfg.color}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between cursor-pointer" onClick={() => setExpandedPkg(isExpanded ? null : pkg.id)}>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-2xl">{cfg.icon}</span>
                        <div>
                          <h2 className={`text-lg font-bold ${cfg.accent}`}>{pkg.name}</h2>
                          <p className="text-sm text-gray-700">{pkg.size_range} · {pkg.org_size} organizations</p>
                        </div>
                      </div>
                      <p className="text-sm text-gray-700 mb-3">{pkg.description}</p>
                      <div className="flex items-center gap-2">
                        <Badge className={cfg.badge}>{pkg.priority_modules.length} modules</Badge>
                        <Badge variant="outline" className="text-xs">
                          {pkg.priority_modules.filter(m => m.priority === 'essential').length} essential
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {pkg.priority_modules.filter(m => m.priority === 'recommended').length} recommended
                        </Badge>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" aria-label={isExpanded ? `Collapse ${pkg.name} details` : `Expand ${pkg.name} details`}>
                      {isExpanded ? <ChevronUp className="h-5 w-5" aria-hidden="true" /> : <ChevronDown className="h-5 w-5" aria-hidden="true" />}
                    </Button>
                  </div>

                  {isExpanded && (
                    <div className="mt-4 pt-4 border-t space-y-3">
                      <p className="text-sm font-semibold text-gray-700">Included Modules</p>
                      {pkg.priority_modules.map((pm) => {
                        const mod = getModuleById(pm.module_id);
                        if (!mod) return null;
                        return (
                          <div key={pm.module_id} className="flex items-start gap-3 p-3 bg-white border rounded-lg">
                            <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ backgroundColor: mod.color }}>
                              {mod.order}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <h3 className="font-medium text-gray-900 text-sm">{mod.title}</h3>
                                <Badge className={pm.priority === 'essential' ? 'bg-red-100 text-red-700 text-xs' : 'bg-gray-100 text-gray-700 text-xs'}>
                                  {pm.priority}
                                </Badge>
                              </div>
                              <p className="text-xs text-gray-700 mt-0.5">{mod.title_el}</p>
                              <p className="text-xs text-gray-700 mt-1">{mod.description}</p>
                              <div className="flex items-center gap-3 mt-2 text-xs text-gray-700">
                                <span className="flex items-center gap-1"><BookOpen className="h-3 w-3" aria-hidden="true" /> {mod.resource_count} resources</span>
                                <span>{mod.category}</span>
                                {pm.reason && <span className="italic">— {pm.reason}</span>}
                              </div>
                              <Button variant="outline" size="sm" className="mt-2 text-xs h-7" onClick={(e) => { e.stopPropagation(); handleViewContent(mod.id); }} data-testid={`view-content-${mod.id}`}>
                                <BookOpen className="h-3 w-3 mr-1" /> View Training Material
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>

        {/* ==================== ALL MODULES TAB ==================== */}
        <TabsContent value="modules" className="space-y-3 mt-4">
          {modules.map((mod) => (
            <Card key={mod.id} data-testid={`module-${mod.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0" style={{ backgroundColor: mod.color }}>
                    {mod.order}
                  </div>
                  <div className="flex-1">
                    <h2 className="font-semibold text-gray-900">{mod.title}</h2>
                    <p className="text-xs text-gray-700">{mod.title_el}</p>
                    <p className="text-sm text-gray-700 mt-1">{mod.description}</p>
                    <div className="flex items-center gap-3 mt-2">
                      <Badge variant="outline" className="text-xs">{mod.category}</Badge>
                      <span className="flex items-center gap-1 text-xs text-gray-700"><BookOpen className="h-3 w-3" aria-hidden="true" /> {mod.resource_count} resources</span>
                      <div className="flex gap-1">
                        {mod.resource_types.map(rt => (
                          <Badge key={rt} variant="secondary" className="text-xs">{rt}</Badge>
                        ))}
                      </div>
                    </div>
                    <Button variant="outline" size="sm" className="mt-3 text-xs h-7" onClick={() => handleViewContent(mod.id)} data-testid={`view-all-content-${mod.id}`}>
                      <BookOpen className="h-3 w-3 mr-1" /> View Training Material
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* ==================== ENROLLMENT OVERSIGHT TAB ==================== */}
        <TabsContent value="oversight" className="space-y-4 mt-4">
          {/* Package Distribution */}
          {stats?.package_distribution && Object.keys(stats.package_distribution).length > 0 && (
            <Card>
              <CardContent className="p-4">
                <p className="font-semibold text-gray-800 mb-3">Package Distribution</p>
                <div className="flex flex-wrap gap-3">
                  {Object.entries(stats.package_distribution).map(([name, count]) => (
                    <div key={name} className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border">
                      <Package className="h-4 w-4 text-gray-700" />
                      <span className="text-sm font-medium text-gray-700">{name}</span>
                      <span className="text-sm text-gray-700">{count}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Search and Sort */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex-1 relative">
                  <label htmlFor="cb-enrollments-search" className="sr-only">Search enrollments</label>
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-700" aria-hidden="true" />
                  <Input id="cb-enrollments-search" aria-label="Search enrollments" data-testid="search-enrollments" placeholder="Search by name or organization..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-700">Sort by:</span>
                  {['progress', 'name', 'activity'].map((s) => (
                    <Button key={s} variant={sortBy === s ? 'default' : 'outline'} size="sm" onClick={() => handleSort(s)} className={sortBy === s ? 'bg-gray-900 text-white' : ''}>
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </Button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Enrollment Cards */}
          {filteredEnrollments.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16">
                <GraduationCap className="h-12 w-12 text-gray-400 mb-4" aria-hidden="true" />
                <h2 className="text-lg font-semibold text-gray-800 mb-2">No Enrollments Yet</h2>
                <p className="text-gray-700 text-center max-w-md mb-4">No social actors have been enrolled in capacity building packages yet.</p>
                {(user?.role === 'administrator' || user?.role === 'project_manager') && (
                  <Button onClick={handleSeed} className="bg-blue-600 hover:bg-blue-700">Initialize Sample Data</Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {filteredEnrollments.map((enrollment) => (
                <Card key={enrollment.id} data-testid={`enrollment-${enrollment.id}`} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                        <Users className="h-5 w-5 text-orange-700" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-gray-900">{enrollment.organization_name}</span>
                          <Badge variant="outline" className={enrollmentPackageColors[enrollment.package_name] || 'border-gray-300 text-gray-700'}>{enrollment.package_name}</Badge>
                        </div>
                        <p className="text-sm text-gray-700 truncate">{enrollment.actor_name} · {enrollment.email}</p>
                      </div>
                      <div className="hidden md:flex items-center gap-3 w-48">
                        <Progress value={enrollment.progress_percentage || 0} className="flex-1 h-2" />
                        <span className="text-sm font-medium text-gray-700 w-10 text-right">{enrollment.progress_percentage || 0}%</span>
                      </div>
                      <div className="hidden md:block text-center w-16">
                        <p className="text-sm font-medium text-gray-700">{enrollment.modules_completed || 0}/{enrollment.total_modules || 10}</p>
                        <p className="text-xs text-gray-700">modules</p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => setExpandedId(expandedId === enrollment.id ? null : enrollment.id)} aria-label={expandedId === enrollment.id ? `Collapse ${enrollment.organization_name} modules` : `Expand ${enrollment.organization_name} modules`}>
                        {expandedId === enrollment.id ? <ChevronUp className="h-4 w-4" aria-hidden="true" /> : <ChevronDown className="h-4 w-4" aria-hidden="true" />}
                      </Button>
                    </div>
                    {/* Mobile progress */}
                    <div className="md:hidden mt-3">
                      <div className="flex items-center gap-3">
                        <Progress value={enrollment.progress_percentage || 0} className="flex-1 h-2" />
                        <span className="text-sm font-medium">{enrollment.progress_percentage || 0}%</span>
                        <span className="text-xs text-gray-700">{enrollment.modules_completed || 0}/{enrollment.total_modules || 10}</span>
                      </div>
                    </div>
                    {/* Expanded module list */}
                    {expandedId === enrollment.id && (
                      <div className="mt-4 pt-4 border-t">
                        <p className="text-sm font-semibold text-gray-700 mb-3">Module Progress</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {modules.map((mod) => {
                            const completed = (enrollment.completed_modules || []).includes(mod.id);
                            return (
                              <div key={mod.id} className={`flex items-center gap-2 p-2 rounded-lg text-sm ${completed ? 'bg-green-50 text-green-800' : 'bg-gray-50 text-gray-700'}`}>
                                <div className={`w-2 h-2 rounded-full ${completed ? 'bg-green-700' : 'bg-gray-300'}`} />
                                <span>{mod.order}. {mod.title}</span>
                                {completed && <Badge className="ml-auto bg-green-100 text-green-700 text-xs">Done</Badge>}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CapacityBuilding;
